import {
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Box,
} from '@chakra-ui/react';
import { AiLoader } from '@/assets/icons/ai';
import { BubbleMenu } from '@tiptap/react';
import { Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { Star06 } from '@kula-ai/icons/solid/Star06';
import { Edit03 } from '@kula-ai/icons/dual-tone/Edit03';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { Recording01 } from '@kula-ai/icons/outline/Recording01';
import { RefreshCcw01 } from '@kula-ai/icons/outline/RefreshCcw01';
import { AlignLeft } from '@kula-ai/icons/outline/AlignLeft';
import { Menu03 } from '@kula-ai/icons/outline/Menu03';
// import { useTextMenuStates } from './EditorBubbleMenuHelpers';
import { Upload03 } from '@kula-ai/icons/solid/Upload03';

import './EditorBubbleMenu.css';
import { useField } from 'formik';
import { KText } from '@/ui-library/components';

// hack to find if full list is selected
function checkIfFullList(a, b) {
  // Count the number of <li> tags in 'b'
  const liCount = (b.match(/<li[^>]*>/g) || []).length;

  // If 'b' contains only one <li> tag, skip further validation and return true
  if (liCount === 1) {
    return false;
  }
  const [firstPart, lastPart] = a.split(b);
  if (lastPart && (lastPart[1] === undefined || lastPart?.startsWith('<li'))) {
    return false;
  }
  const ulPattern = /<ul dir="[^"]*">$/;
  return ulPattern.test(firstPart);
}

function removeLiTag(str) {
  // Create a regular expression to match the entire <li> tag
  const liTagRegex = /<li[^>]*>(.*?)<\/li>/;
  // Replace the matched <li> tag with the inner HTML content
  return str.replace(liTagRegex, '$1');
}

export const EditorBubbleMenu = ({
  enabled,
  editor,
  selectedText,
  selectedHtml,
  setBubbleConfig,
  aiMessageGeneration,
  apiConfig,
  bubbleMenuRef,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(undefined);

  const [, { value: liveEditorHtmlString }] = useField('text');

  if (!enabled || !editor) return null;

  const bubbleItemConfigs = [
    {
      label: 'Edit',
      id: 'ai_action',
      leftIcon: Edit03,
      rightIcon: ChevronDown,
      subItems: [
        { label: 'Rewrite', value: 'rewrite', icon: RefreshCcw01 },
        { label: 'Make shorter', value: 'shrink', icon: Menu03 },
        { label: 'Make longer', value: 'elaborate', icon: AlignLeft },
      ],
    },
    {
      label: 'Change tone',
      id: 'tone',
      leftIcon: Recording01,
      rightIcon: ChevronDown,
      subItems: [
        { label: 'Professional', value: 'change_tone', tone: 'professional' },
        { label: 'Casual', value: 'change_tone', tone: 'casual' },
        { label: 'Enthusiastic', value: 'change_tone', tone: 'enthusiastic' },
        { label: 'Persuasive', value: 'change_tone', tone: 'persuasive' },
      ],
    },
  ];

  const handleItemClick = async (id, value, tone = 'professional') => {
    const { urls, job_meta, job_id } = apiConfig;
    const payload = {
      ai_action: value,
      job_meta,
      job_id,
      //todo: temp fix for ul issues
      input_content: selectedHtml
        .replaceAll('<ul>', '')
        .replaceAll('</ul>', ''),
    };
    payload.tone = tone;
    try {
      setIsLoading(true);
      const message = await aiMessageGeneration(urls?.aiActions, payload);
      if (message) {
        const editorMessage = message.replaceAll('\n', '');

        const processedMsg = checkIfFullList(liveEditorHtmlString, selectedHtml)
          ? editorMessage
          : removeLiTag(editorMessage);

        editor.commands.deleteSelection();
        editor.commands.insertContent(processedMsg);
      }
    } finally {
      setIsLoading(false);
    }

    //todo: might need in future
    setBubbleConfig((prev) => ({
      ...prev,
      selectedHtml,
      selectedText,
      [id]: value,
    }));
  };

  const handlePromptEdit = async () => {
    const { urls, job_meta, job_id, tone } = apiConfig;
    const payload = {
      job_meta,
      job_id,
      input_content: selectedHtml,
      user_prompt: inputRef?.current?.value,
    };
    if (tone) payload.tone = tone;

    try {
      setIsLoading(true);
      const message = await aiMessageGeneration(urls?.promptEdit, payload);
      const editorMessage = message?.replaceAll('\n', '');

      if (message) {
        editor.commands.deleteSelection();
        editor.commands.insertContent(editorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderBubbleItem = (item = {}) => {
    const { id, label, subItems, leftIcon: LIcon, rightIcon: RIcon } = item;
    return (
      <Menu key={id}>
        <MenuButton
          as={Button}
          variant="secondary"
          rightIcon={<RIcon width="16px" height="16px" color="primary-500" />}
          leftIcon={<LIcon width="16px" height="16px" color="primary-500" />}
        >
          {label}
        </MenuButton>
        <MenuList zIndex="2">
          {subItems.map((subItem = {}, idx) => {
            const { label, icon, value, tone } = subItem;
            return (
              <MenuItem
                key={idx}
                onClick={() => handleItemClick(id, value, tone)}
              >
                <Flex gap=".3rem" alignItems="center">
                  {icon && <Icon as={icon} color="primary-500" />}
                  <KText color="primary-500" fontSize="sm">
                    {label}
                  </KText>
                </Flex>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    );
  };

  const renderBubbleActions = () => {
    return (
      <Flex minW={isLoading ? '200px' : '400px'} flexDir="column" gap=".5em">
        {isLoading ? (
          // <Box
          //   textAlign="center"
          //   size="sm-medium"
          //   opacity=".5"
          //   color="ats-primary"
          // >
          //   Generating content...
          // </Box>
          <Flex alignItems="center" justifyContent="center" gap=".5em">
            <Icon as={AiLoader} h="50px" w="50px" />
            <KText
              size="xxl-regular"
              style={{
                background:
                  '-webkit-linear-gradient(91.04deg, #7CD0FF 5.69%, #6249F9 88.5%)',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              AI is Writing
            </KText>
          </Flex>
        ) : (
          <>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={Star06} color="ats-primary" />
              </InputLeftElement>
              <Input
                type="text"
                ref={inputRef}
                placeholder="Ask AI to edit"
                autoFocus={true}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Handle your onChange logic here
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePromptEdit();
                  }
                }}
              />
              <InputRightElement width="2rem" cursor="pointer">
                <Icon
                  as={Upload03}
                  onClick={handlePromptEdit}
                  color="ats-primary"
                  w="25px"
                  h="25px"
                />
              </InputRightElement>
            </InputGroup>
            <Flex gap=".5rem">{bubbleItemConfigs?.map(renderBubbleItem)}</Flex>
          </>
        )}
      </Flex>
    );
  };

  return (
    <Box>
      {editor && (
        <BubbleMenu
          editor={editor}
          tippyOptions={{
            popperOptions: {
              placement: 'top-start',
              onShow: () => {
                // Additional focus attempt when menu shows
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 100);
              },
            },
          }}
          updateDelay={100}
          // shouldShow={(...props) =>
          //   editorStates.shouldShow(props) && isIntersecting
          // }
        >
          <Menu preventOverflow={false}>
            <MenuButton type="button" ref={bubbleMenuRef}>
              <Flex
                bgColor="#E6E5F9"
                border="1px solid"
                borderColor="ats-primary"
                borderRadius="10px"
                w="24px"
                h="24px"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
              >
                <Icon
                  width="14px"
                  height="14px"
                  as={Star06}
                  color="ats-primary"
                />
              </Flex>
            </MenuButton>
            <MenuList
              onClick={(e) => e.stopPropagation()}
              zIndex={1}
              borderColor="ats-primary"
              borderRadius="6px"
            >
              {renderBubbleActions()}
            </MenuList>
          </Menu>
        </BubbleMenu>
      )}
    </Box>
  );
};
